// Reads the founder's onboarding questionnaire (the m8t_onboarding block) from the
// Foundry conversation data plane and renders a brain company-profile.md + its index line.

import { INTAKE_AGENT_NAME } from "@m8t-stack/api-contract";

export interface PendingRequest {
  type: "quota";
  model: string;
  region: string;
  consent: "submit" | "prepare";
  company_address: { address: string; city: string; postal_code: string; country: string } | null;
}

export interface OnboardingBlock {
  schema_version?: string;
  company_stage?: string;
  icp?: string;
  industry?: string;
  team_size?: string;
  context?: string;
  founder_name?: string;
  founder_email?: string;
  advisor_name?: string;
  advisor_email?: string;
  // v3 additions
  company_name?: string;
  azure_identity_note?: string;
  pending_requests?: PendingRequest[];
}

export interface CanonicalOnboardingBlock extends OnboardingBlock {
  schema_version: string;
  context: string;
  founder_name: string;
  founder_email: string;
  advisor_name: string;
  advisor_email: string;
}

export interface ParsedOnboardingArtifact {
  block: CanonicalOnboardingBlock;
  /** Exact canonical assistant text, including the durable machine artifact. */
  machineText: string;
  /** Human wrap-up only, suitable for speech. */
  speechText: string;
}

export type BlockRejectionReason =
  | "unknown-schema-version"
  | "unexpected-key"
  | "missing-key"
  | "non-string-value"
  | "disallowed-value"
  | "too-many-pending-requests"
  | "malformed-json"
  | "multiple-artifacts"
  | "unreadable-fences"
  | "mistagged-fence"
  | "unfenced-artifact"
  | "no-artifact";

/** Human-readable text for an operator-facing line. Keep it actionable, never blaming. */
export function describeBlockRejection(reason: BlockRejectionReason): string {
  switch (reason) {
    case "unknown-schema-version": return "the onboarding block's schema_version was missing or unrecognized";
    case "unexpected-key": return "the onboarding JSON carried a field it shouldn't have";
    case "missing-key": return "the onboarding block was missing a required field";
    case "non-string-value": return "the onboarding block held a value of the wrong type";
    case "disallowed-value": return "an onboarding block field held a value outside its allowed set";
    case "too-many-pending-requests": return "the onboarding block listed more pending requests than the one allowed";
    case "malformed-json": return "the onboarding block's JSON could not be safely parsed — check for duplicate keys, excessive nesting, or a syntax error";
    case "multiple-artifacts": return "more than one onboarding block was found in your onboarding conversation";
    case "unreadable-fences": return "the message's code fences could not be reliably delimited, so any onboarding block inside them could not be safely read";
    case "mistagged-fence": return "the onboarding block was inside a code fence that wasn't tagged json, so it could not be safely read";
    case "unfenced-artifact": return "the onboarding block appeared outside of any code fence, so it could not be safely read";
    case "no-artifact": return "no onboarding block was found";
  }
}

type BlockOutcome =
  | { ok: true; block: CanonicalOnboardingBlock }
  | { ok: false; reason: BlockRejectionReason };

export const COMPANY_PROFILE_PATH = "memory/company-profile.md";

export const DEFAULT_MEMORY_INDEX_HEADER = [
  `# Memory index`,
  ``,
  `> Your memories, newest first. The summary on each line is usually enough to answer — open the linked file only when you need full detail. Each path is repo-root; copy it verbatim, never invent one.`,
  ``,
].join("\n");

const ONBOARDING_BLOCK_KEYS = [
  "schema_version",
  "company_stage",
  "icp",
  "industry",
  "team_size",
  "context",
  "founder_name",
  "founder_email",
  "advisor_name",
  "advisor_email",
] as const;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * JSON.parse silently keeps only the last duplicate object key. Validate the
 * token structure first so accepting an onboarding artifact is lossless.
 */
function hasValidUniqueJsonKeys(source: string): boolean {
  let offset = 0;

  const skipWhitespace = () => {
    while (/\s/.test(source[offset] ?? "")) offset += 1;
  };

  const parseString = (): string | null => {
    if (source[offset] !== '"') return null;
    const start = offset;
    offset += 1;
    let escaped = false;
    while (offset < source.length) {
      const char = source[offset];
      if (escaped) {
        escaped = false;
        offset += 1;
        continue;
      }
      if (char === "\\") {
        escaped = true;
        offset += 1;
        continue;
      }
      if (char === '"') {
        offset += 1;
        try {
          const parsed: unknown = JSON.parse(source.slice(start, offset));
          return typeof parsed === "string" ? parsed : null;
        } catch {
          return null;
        }
      }
      offset += 1;
    }
    return null;
  };

  const parseValue = (depth: number): boolean => {
    if (depth > 32) return false;
    skipWhitespace();
    const char = source[offset];
    if (char === '"') return parseString() !== null;
    if (char === "{") {
      offset += 1;
      skipWhitespace();
      const keys = new Set<string>();
      if (source[offset] === "}") {
        offset += 1;
        return true;
      }
      for (;;) {
        skipWhitespace();
        const key = parseString();
        if (key === null) return false;
        if (keys.has(key)) return false;
        keys.add(key);
        skipWhitespace();
        if (source[offset] !== ":") return false;
        offset += 1;
        if (!parseValue(depth + 1)) return false;
        skipWhitespace();
        if (source[offset] === "}") {
          offset += 1;
          return true;
        }
        if (source[offset] !== ",") return false;
        offset += 1;
      }
    }
    if (char === "[") {
      offset += 1;
      skipWhitespace();
      if (source[offset] === "]") {
        offset += 1;
        return true;
      }
      for (;;) {
        if (!parseValue(depth + 1)) return false;
        skipWhitespace();
        if (source[offset] === "]") {
          offset += 1;
          return true;
        }
        if (source[offset] !== ",") return false;
        offset += 1;
      }
    }

    const primitiveStart = offset;
    while (offset < source.length && !/[\s,}\]]/.test(source[offset] ?? "")) offset += 1;
    return offset > primitiveStart;
  };

  if (!parseValue(0)) return false;
  skipWhitespace();
  if (offset !== source.length) return false;
  try {
    JSON.parse(source);
    return true;
  } catch {
    return false;
  }
}

const V3_BLOCK_KEYS = ["schema_version", "founder", "company", "advisor_email", "pending_requests"] as const;
const V3_FOUNDER_KEYS = ["name", "email", "azure_identity_note"] as const;
const V3_COMPANY_KEYS = ["name", "one_liner"] as const;
const V3_ADDRESS_KEYS = ["address", "city", "postal_code", "country"] as const;
const V3_REQUEST_KEYS = ["type", "model", "region", "consent", "company_address"] as const;

/** Validate that `value` is a record with exactly `keys`, all string-valued. */
function exactStringRecord(value: unknown, keys: readonly string[]): BlockRejectionReason | null {
  if (!isRecord(value)) return "non-string-value";
  const present = new Set(Object.keys(value));
  for (const key of keys) if (!present.has(key)) return "missing-key";
  if (present.size !== keys.length) return "unexpected-key";
  for (const key of keys) if (typeof value[key] !== "string") return "non-string-value";
  return null;
}

/** v3 allows at most one pending request; a second entry is too many, not an unexpected shape. */
function canonicalPendingRequests(
  value: unknown,
): { ok: true; requests: PendingRequest[] } | { ok: false; reason: BlockRejectionReason } {
  if (!Array.isArray(value)) return { ok: false, reason: "non-string-value" };
  if (value.length > 1) return { ok: false, reason: "too-many-pending-requests" };
  const requests: PendingRequest[] = [];
  for (const entry of value) {
    if (!isRecord(entry)) return { ok: false, reason: "non-string-value" };
    const present = new Set(Object.keys(entry));
    for (const key of V3_REQUEST_KEYS) {
      if (!present.has(key)) return { ok: false, reason: "missing-key" };
    }
    if (present.size !== V3_REQUEST_KEYS.length) return { ok: false, reason: "unexpected-key" };
    if (entry.type !== "quota") return { ok: false, reason: "disallowed-value" };
    if (entry.consent !== "submit" && entry.consent !== "prepare") return { ok: false, reason: "disallowed-value" };
    if (typeof entry.model !== "string" || typeof entry.region !== "string") {
      return { ok: false, reason: "non-string-value" };
    }
    if (entry.company_address !== null) {
      const bad = exactStringRecord(entry.company_address, V3_ADDRESS_KEYS);
      if (bad) return { ok: false, reason: bad };
    }
    requests.push(entry as unknown as PendingRequest);
  }
  return { ok: true, requests };
}

/** Validate a v3 block and flatten it onto the shared internal shape. */
function canonicalV3Block(value: Record<string, unknown>): BlockOutcome {
  const present = new Set(Object.keys(value));
  for (const key of V3_BLOCK_KEYS) if (!present.has(key)) return { ok: false, reason: "missing-key" };
  if (present.size !== V3_BLOCK_KEYS.length) return { ok: false, reason: "unexpected-key" };

  const founderBad = exactStringRecord(value.founder, V3_FOUNDER_KEYS);
  if (founderBad) return { ok: false, reason: founderBad };
  const companyBad = exactStringRecord(value.company, V3_COMPANY_KEYS);
  if (companyBad) return { ok: false, reason: companyBad };
  if (value.advisor_email !== null && typeof value.advisor_email !== "string") {
    return { ok: false, reason: "non-string-value" };
  }
  const requests = canonicalPendingRequests(value.pending_requests);
  if (!requests.ok) return requests;

  const founder = value.founder as Record<string, string>;
  const company = value.company as Record<string, string>;

  // v3 carries no stage/ICP/industry/team-size — they are left UNDEFINED, not "", so the
  // renderers can omit those bullets entirely instead of printing a placeholder for a
  // question that was never asked.
  return {
    ok: true,
    block: {
      schema_version: "3",
      founder_name: founder.name,
      founder_email: founder.email,
      azure_identity_note: founder.azure_identity_note,
      company_name: company.name,
      context: company.one_liner,
      advisor_name: "",
      advisor_email: typeof value.advisor_email === "string" ? value.advisor_email : "",
      pending_requests: requests.requests,
    },
  };
}

/**
 * `schema_version` is checked before the key-set comparison so a future-version block
 * reports `unknown-schema-version` (true and actionable) rather than `unexpected-key`
 * (technically true, misleading). Missing-key is checked before unexpected-key for the
 * same reason. Preserve this ordering.
 */
function canonicalBlock(value: unknown): BlockOutcome {
  if (!isRecord(value)) return { ok: false, reason: "non-string-value" };
  if (value.schema_version === "3") return canonicalV3Block(value);
  if (value.schema_version !== "2") return { ok: false, reason: "unknown-schema-version" };
  const keys = new Set(Object.keys(value));
  for (const key of ONBOARDING_BLOCK_KEYS) {
    if (!keys.has(key)) return { ok: false, reason: "missing-key" };
  }
  if (keys.size !== ONBOARDING_BLOCK_KEYS.length) return { ok: false, reason: "unexpected-key" };
  for (const key of ONBOARDING_BLOCK_KEYS) {
    if (typeof value[key] !== "string") return { ok: false, reason: "non-string-value" };
  }
  return { ok: true, block: value as unknown as CanonicalOnboardingBlock };
}

/** Parse exactly one persona-authored fenced onboarding artifact (schema v2 or v3), naming why it failed. */
export function parseOnboardingArtifactResult(
  machineText: string,
): { ok: true; artifact: ParsedOnboardingArtifact } | { ok: false; reason: BlockRejectionReason } {
  const fencePattern = /^```([^\r\n]*)\r?\n([\s\S]*?)^```[ \t]*(?=\r?$)/gm;
  const fences = [...machineText.matchAll(fencePattern)];
  const jsonFenceStarts = [...machineText.matchAll(/^```[ \t]*json[ \t]*\r?$/gm)];
  const matchedJsonFences = fences.filter((match) => match[1].trim() === "json");
  if (jsonFenceStarts.length !== matchedJsonFences.length) return { ok: false, reason: "unreadable-fences" };
  if (fences.some((match) => match[1].trim() !== "json" && /"m8t_onboarding"\s*:/.test(match[2]))) {
    return { ok: false, reason: "mistagged-fence" };
  }

  let outsideFences = "";
  let previousEnd = 0;
  for (const fence of fences) {
    const start = fence.index;
    outsideFences += machineText.slice(previousEnd, start);
    previousEnd = start + fence[0].length;
  }
  outsideFences += machineText.slice(previousEnd);
  if (/"m8t_onboarding"\s*:/.test(outsideFences)) return { ok: false, reason: "unfenced-artifact" };

  const artifacts: { block: CanonicalOnboardingBlock; start: number; end: number }[] = [];
  for (const fence of matchedJsonFences) {
    const json = fence[2].trim();
    let parsed: unknown;
    if (!hasValidUniqueJsonKeys(json)) {
      if (json.includes("m8t_onboarding")) return { ok: false, reason: "malformed-json" };
      continue;
    }
    try {
      parsed = JSON.parse(json);
    } catch {
      if (json.includes("m8t_onboarding")) return { ok: false, reason: "malformed-json" };
      continue;
    }
    if (!isRecord(parsed) || !Object.hasOwn(parsed, "m8t_onboarding")) continue;
    if (Object.keys(parsed).length !== 1) return { ok: false, reason: "unexpected-key" };
    const outcome = canonicalBlock(parsed.m8t_onboarding);
    if (!outcome.ok) return outcome;
    const start = fence.index;
    artifacts.push({ block: outcome.block, start, end: start + fence[0].length });
  }
  if (artifacts.length === 0) return { ok: false, reason: "no-artifact" };
  if (artifacts.length > 1) return { ok: false, reason: "multiple-artifacts" };

  const artifact = artifacts[0];
  const before = machineText.slice(0, artifact.start).trimEnd();
  const after = machineText.slice(artifact.end).trimStart();
  return {
    ok: true,
    artifact: {
      block: artifact.block,
      machineText,
      speechText: [before, after].filter((part) => part.length > 0).join("\n").trim(),
    },
  };
}

/** Parse exactly one persona-authored fenced onboarding artifact (schema v2 or v3), or null if it was rejected. */
export function parseOnboardingArtifact(machineText: string): ParsedOnboardingArtifact | null {
  const result = parseOnboardingArtifactResult(machineText);
  return result.ok ? result.artifact : null;
}

export function parseOnboardingBlock(text: string): CanonicalOnboardingBlock | null {
  return parseOnboardingArtifact(text)?.block ?? null;
}

export interface FindProfileResult {
  hadIntake: boolean;
  block: CanonicalOnboardingBlock | null;
  machineText: string | null;
  speechText: string | null;
  /** Why a present-but-unreadable block was rejected. null when there was nothing to reject. */
  rejection: BlockRejectionReason | null;
  /**
   * Set only when more than one valid onboarding block was found in the scanned conversation —
   * the count of older valid blocks the seeded (newest) one superseded. A founder re-stating
   * their answers is not an error, so this never blocks seeding; it exists purely so a caller
   * can note that supersession happened instead of staying silent about it. Absent on the
   * ordinary single-block path.
   */
  supersededCount?: number;
}

const EMPTY_PROFILE_RESULT: FindProfileResult = {
  hadIntake: false,
  block: null,
  machineText: null,
  speechText: null,
  rejection: null,
};

interface CursorPage {
  data: unknown[];
  has_more?: unknown;
  last_id?: unknown;
}

async function readCursorPages(args: {
  url: string;
  headers: Record<string, string>;
  fetchImpl: typeof fetch;
}): Promise<unknown[] | null> {
  const all: unknown[] = [];
  const cursors = new Set<string>();
  let url = args.url;
  for (let pageNumber = 0; pageNumber < 100; pageNumber += 1) {
    let response: Response;
    try {
      response = await args.fetchImpl(url, { headers: args.headers });
    } catch {
      return null;
    }
    if (!response.ok) return null;
    let body: unknown;
    try {
      body = JSON.parse(await response.text());
    } catch {
      return null;
    }
    if (!isRecord(body) || !Array.isArray(body.data)) return null;
    const page = body as unknown as CursorPage;
    all.push(...page.data);
    if (page.has_more !== true) {
      if (page.has_more === undefined || page.has_more === false) return all;
      return null;
    }
    if (typeof page.last_id !== "string" || page.last_id.trim().length === 0) return null;
    if (cursors.has(page.last_id)) return null;
    cursors.add(page.last_id);
    const next = new URL(url);
    next.searchParams.set("after", page.last_id);
    url = next.toString();
  }
  return null;
}

function decodeFoundryUser(token: string): string | null {
  const parts = token.split(".");
  if (parts.length !== 3 || parts.some((part) => part.length === 0) || !/^[A-Za-z0-9_-]+$/.test(parts[1] ?? "")) return null;
  try {
    const payload: unknown = JSON.parse(Buffer.from(parts[1] ?? "", "base64url").toString("utf8"));
    if (!isRecord(payload)) return null;
    const claim = payload.upn ?? payload.preferred_username ?? payload.email;
    return typeof claim === "string" && claim.trim().length > 0 ? claim : null;
  } catch {
    return null;
  }
}

function timestamp(value: unknown): number {
  if (typeof value === "number" && Number.isFinite(value)) return value < 1_000_000_000_000 ? value * 1_000 : value;
  if (typeof value === "string") {
    const parsed = Date.parse(value);
    return Number.isFinite(parsed) ? parsed : Number.NEGATIVE_INFINITY;
  }
  return Number.NEGATIVE_INFINITY;
}

function orderNewest<T extends { createdAt: number; id: string; ordinal: number }>(values: T[]): T[] {
  if (!values.every((value) => Number.isFinite(value.createdAt))) return values;
  return values.sort((a, b) => b.createdAt - a.createdAt || a.ordinal - b.ordinal || b.id.localeCompare(a.id));
}

/**
 * List the founder's conversations, keep the intake ones (the webapp tags every
 * conversation metadata.agent="ezra-intake"), newest first, and scan each one's /items
 * for the onboarding block. `/items` (not `/messages`) is where the message text lives.
 */
export async function findOnboardingProfile(args: {
  endpoint: string; token: string; fetchImpl?: typeof fetch;
}): Promise<FindProfileResult> {
  // Fail fast on a token that isn't a well-formed identity JWT, before making any
  // network call. The decoded identity is no longer used to scope conversations (see the
  // app + agent match below) — this guard only verifies the token structure, not authentication.
  if (!decodeFoundryUser(args.token)) return { ...EMPTY_PROFILE_RESULT };
  const doFetch = args.fetchImpl ?? fetch;
  const H = { Authorization: `Bearer ${args.token}` };
  const base = args.endpoint.replace(/\/+$/, "");
  const listed = await readCursorPages({
    url: `${base}/openai/v1/conversations?order=desc&limit=100`,
    headers: H,
    fetchImpl: doFetch,
  });
  if (!listed) return { ...EMPTY_PROFILE_RESULT };

  const conversations = orderNewest(listed.flatMap((value, ordinal) => {
    if (!isRecord(value) || typeof value.id !== "string" || value.id.length === 0 || !isRecord(value.metadata)) return [];
    const metadata = value.metadata;
    // Scoped to app + agent only, deliberately not to a per-conversation identity. This
    // capture path runs solely against the founder's own freshly bootstrapped Foundry
    // project (the endpoint comes from the install status blob or an explicit --endpoint),
    // and the web surface authenticates as the same account as `az login` — so within that
    // project, every conversation tagged app="m8t-webapp" and agent="ezra-intake" is this
    // founder's own intake. The production web writer (apps/web/lib/conversations.ts
    // createConversation()) is always called with a token-getter, never a UPN string, so it
    // writes metadata.userId="" and carries ownership in a hashed metadata.ownerKey instead;
    // matching on userId here dropped every real conversation. We deliberately don't
    // reconstruct that hashed ownerKey — this scope would over-match on a shared or
    // multi-tenant project, potentially seeding from a different founder's intake — which this single-founder local bootstrap path never targets.
    if (metadata.app !== "m8t-webapp" || metadata.agent !== INTAKE_AGENT_NAME) return [];
    return [{
      id: value.id,
      createdAt: timestamp(value.created_at) !== Number.NEGATIVE_INFINITY
        ? timestamp(value.created_at)
        : timestamp(metadata.createdAt),
      ordinal,
    }];
  }));

  const conversation = conversations.at(0);
  if (conversation === undefined) return { ...EMPTY_PROFILE_RESULT };
  const items = await readCursorPages({
    url: `${base}/openai/v1/conversations/${encodeURIComponent(conversation.id)}/items?order=desc&limit=100`,
    headers: H,
    fetchImpl: doFetch,
  });
  if (!items) return { hadIntake: true, block: null, machineText: null, speechText: null, rejection: null };

  const assistantItems = orderNewest(items.flatMap((value, ordinal) => {
    if (!isRecord(value) || value.type !== "message" || value.role !== "assistant" || !Array.isArray(value.content)) return [];
    const id = typeof value.id === "string" ? value.id : "";
    return [{ value, id, createdAt: timestamp(value.created_at), ordinal }];
  }));
  const artifacts: ParsedOnboardingArtifact[] = [];
  let rejection: BlockRejectionReason | null = null;
  for (const item of assistantItems) {
    const content = item.value.content as unknown[];
    const machineText = content.flatMap((part) => {
      if (!isRecord(part)) return [];
      if (typeof part.text === "string" && part.text.length > 0) return [part.text];
      if (typeof part.transcript === "string") return [part.transcript];
      return [];
    }).join("\n");
    const result = parseOnboardingArtifactResult(machineText);
    if (result.ok) artifacts.push(result.artifact);
    else if (machineText.includes("m8t_onboarding")) rejection ??= result.reason;
  }
  // A successfully-parsed valid block always wins. Prose that merely mentions the marker, or
  // a malformed draft, never poisons a valid block sitting elsewhere in the conversation —
  // only the absence of any valid block surfaces a rejection reason at all.
  if (artifacts.length === 0) {
    return {
      hadIntake: true, block: null, machineText: null, speechText: null,
      rejection: rejection ?? "no-artifact",
    };
  }
  // assistantItems is newest-first, so artifacts[0] is the newest valid block. If a founder
  // re-stated their answers (more than one valid block in the conversation), that is not an
  // error: seed the newest and just note how many older ones it superseded.
  const artifact = artifacts[0];
  return {
    hadIntake: true,
    block: artifact.block,
    machineText: artifact.machineText,
    speechText: artifact.speechText,
    rejection: null,
    ...(artifacts.length > 1 ? { supersededCount: artifacts.length - 1 } : {}),
  };
}

// undefined = the question was never asked (the short intake) → omit the bullet entirely.
// ""        = asked and left blank (the long questionnaire)   → keep today's em-dash.
// Shared by both memos below so this rule is expressed in exactly one place.
const dash = (v?: string) => (v?.trim() ? v.trim() : "—");
const bullet = (label: string, value: string | undefined) =>
  value === undefined ? [] : [`- **${label}:** ${dash(value)}`];

export function renderCompanyProfile(
  block: OnboardingBlock, now: string = new Date().toISOString(),
): { profileMd: string; memoryIndexLine: string } {
  const profileMd = [
    `---`,
    `type: memory`,
    `title: Company profile`,
    `created: ${now}`,
    `updated: ${now}`,
    `tags: [company-profile, onboarding, founder]`,
    `origin: operator`,
    `---`,
    ``,
    `# Company profile`,
    ``,
    // Named the questionnaire until the questionnaire stopped existing. There is no
    // intake conversation any more — `m8t bootstrap profile` confirms two contact
    // facts and nothing about the company — so the old line described a thing that
    // never happened to this founder.
    `_Seeded at install._`,
    ``,
    ...bullet("Company", block.company_name),
    ...bullet("Stage", block.company_stage),
    ...bullet("ICP", block.icp),
    ...bullet("Industry", block.industry),
    ...bullet("Team size", block.team_size),
    ``,
    `## Context`,
    ``,
    (block.context ?? "").trim(),
    ``,
  ].join("\n");
  // An empty company.name is a valid v3 payload (the parser only checks the field is a
  // string, and the persona template's own placeholder is ""), so `bits` can end up empty.
  // Fall back to a one-liner instead of rendering a bare "Company profile**: . (seeded...)".
  // Scope the fallback to v3 ONLY: an old-version (v2) all-blank block must keep its exact
  // bare-period line, byte-identical to what already lives in existing repos, so a converge
  // re-render never rewrites it. v2 never reaches this fallback — the bare period stands.
  const bits = [block.company_name?.trim(), block.company_stage?.trim(), block.industry?.trim(),
    block.icp?.trim() ? `ICP ${block.icp.trim()}` : undefined]
    .filter(Boolean).join(" · ");
  const summary = bits || (block.schema_version === "3" ? "no details captured at intake" : "");
  const memoryIndexLine = `- \`${COMPANY_PROFILE_PATH}\` — **Company profile**: ${summary}. (seeded from onboarding)`;
  return { profileMd, memoryIndexLine };
}

/** Compare two rendered company-profile docs ignoring the volatile created/updated lines. */
export function companyProfileBodyEquals(a: string, b: string): boolean {
  const strip = (s: string) => stripVolatileFrontmatterTimestamps(s).trim();
  return strip(a) === strip(b);
}

function stripVolatileFrontmatterTimestamps(value: string): string {
  const lines = value.split("\n");
  if (lines[0]?.trim() !== "---") return value;
  const closing = lines.findIndex((line, index) => index > 0 && line.trim() === "---");
  if (closing < 0) return value;
  for (let index = 1; index < closing; index += 1) {
    if (/^(created|updated):/.test(lines[index] ?? "")) lines[index] = (lines[index] ?? "").split(":", 1)[0] + ":";
  }
  return lines.join("\n");
}

/** Prepend an index line above the first list item, replacing any prior one for `targetPath`. */
export function upsertMemoryIndex(existing: string, line: string, targetPath: string = COMPANY_PROFILE_PATH): string {
  const lines = existing.split("\n");
  const target = `\`${targetPath}\``;
  const existingIdx = lines.findIndex((value) => value.includes(target));
  if (existingIdx >= 0) {
    let inserted = false;
    return lines.flatMap((value) => {
      if (!value.includes(target)) return [value];
      if (inserted) return [];
      inserted = true;
      return [line];
    }).join("\n");
  }
  const firstItemIdx = lines.findIndex((l) => /^-\s+`memory\//.test(l));
  if (firstItemIdx >= 0) {
    lines.splice(firstItemIdx, 0, line);
    return lines.join("\n");
  }
  return existing.replace(/\s*$/, "\n\n") + line + "\n";
}

export const FOUNDER_RECORD_PATH = "memory/founder.md";

// The advisor reading this record fills a blank the moment the founder mentions it in
// conversation — that is what "just tell me" points at.
//
// It used to say, at length, that this line must NOT point at `m8t bootstrap seed-profile`,
// because that command re-read the same immutable intake conversation and so could only ever
// reproduce the same blank — unfollowable advice on the path that printed it. That reasoning
// is dead: the answers now come from `m8t bootstrap profile`, which is re-runnable, so a blank
// here IS fixable by a command. The wording still points at conversation because that is the
// lower-friction of the two, not because the other one cannot work. Blanks stay routine — the
// advisor is genuinely optional and the subscription comes from install context.
const NOT_CAPTURED = "_not captured yet — just tell me and I'll add it_";

// Region is never founder-supplied: the persona is explicitly instructed to leave it blank
// at intake (it doesn't know which region the install used) and it is only known once a
// later feature files the request. Even NOT_CAPTURED's honest "just tell me" is wrong here —
// the region is not a fact the founder could supply — so name the only thing that fills it.
const REGION_UNKNOWN_AT_INTAKE = "_not known at intake — filled in when the request is filed_";

/**
 * Render the operator-owned founder identity/contacts record (memory/founder.md).
 * Precedence per field: the questionnaire block wins (founder-confirmed), then the
 * local Azure identity fallback, then a fillable placeholder. subscription_id +
 * team_size come from the install context / existing block, never re-asked.
 */
/** Label prefixes of the bullets the profile step owns. Order is the rendered order. */
const FOUNDER_BULLET_LABELS = {
  name: "- **Founder:** ",
  email: "- **Founder email (company_email):** ",
  advisor: "- **Microsoft Startup Advisor (SA):** ",
  subscription: "- **Azure subscription:** ",
} as const;

/**
 * Rewrite ONLY the contact bullets in an existing founder record, in place.
 *
 * `m8t bootstrap profile` collects two contact facts. Re-rendering the whole document
 * from those two facts is not an update, it is a replacement — on a brain seeded back
 * when the intake agent still ran, it silently drops the team-size bullet and the
 * entire `## Requested at onboarding` block, which records a real quota request and
 * the founder's consent to file it. That file is `origin: operator`; the advisor is
 * told to treat it as authoritative and never rewrite it. We must not either.
 *
 * Only fields we POSITIVELY have are written. An absent value never overwrites a
 * present one: a founder who skips the advisor is saying "I don't know it", not
 * "delete the one you have", and a failed `az` lookup must not blank their name.
 *
 * Returns null when `existing` is not a founder record we recognise, so the caller
 * can fall back to rendering a fresh one.
 */
export function updateFounderContacts(
  existing: string,
  block: OnboardingBlock,
  inputs: {
    subscriptionId?: string;
    /**
     * The founder said there is no advisor, rather than not saying. Writes the
     * fillable placeholder over whatever is there — without it, `--no-advisor`
     * cannot remove an address the advisor is actively emailing.
     */
    advisorCleared?: boolean;
  },
): string | null {
  if (!existing.includes(FOUNDER_BULLET_LABELS.email)) return null;

  const advisorName = (block.advisor_name ?? "").trim();
  const advisorEmail = (block.advisor_email ?? "").trim();
  const advisor = inputs.advisorCleared === true
    ? renderAdvisor(advisorName, advisorEmail, NOT_CAPTURED)
    : renderAdvisor(advisorName, advisorEmail, "");
  const replacements: [string, string][] = [];
  const add = (prefix: string, value: string) => {
    if (value) replacements.push([prefix, prefix + value]);
  };
  add(FOUNDER_BULLET_LABELS.name, (block.founder_name ?? "").trim());
  add(FOUNDER_BULLET_LABELS.email, (block.founder_email ?? "").trim());
  add(FOUNDER_BULLET_LABELS.advisor, advisor);
  add(FOUNDER_BULLET_LABELS.subscription, (inputs.subscriptionId ?? "").trim());

  // Split on the newline but keep any \r with the line, so replacing a bullet in a
  // CRLF document does not leave that one line with a bare \n.
  return existing.split("\n").map((line) => {
    const cr = line.endsWith("\r") ? "\r" : "";
    const bare = cr ? line.slice(0, -1) : line;
    const hit = replacements.find(([prefix]) => bare.startsWith(prefix));
    return hit ? hit[1] + cr : line;
  }).join("\n");
}

/** "Sam Lee <sam@x.com>", either half alone, or `absent` when we have neither. */
function renderAdvisor(name: string, email: string, absent: string): string {
  if (!name && !email) return absent;
  return [name, email ? `<${email}>` : ""].filter(Boolean).join(" ");
}

/**
 * Read the advisor back out of an existing founder record.
 *
 * Needed because a contacts-only update deliberately leaves that bullet alone when
 * the founder did not answer the question. The MEMORY.md index line is rendered
 * from the block, so without recovering what the document actually says the index
 * would report "advisor —" over a record naming a real advisor — two files in the
 * same commit contradicting each other.
 */
export function readAdvisorFromRecord(existing: string): { name: string; email: string } | null {
  const line = existing.split("\n")
    .map((l) => (l.endsWith("\r") ? l.slice(0, -1) : l))
    .find((l) => l.startsWith(FOUNDER_BULLET_LABELS.advisor));
  if (line === undefined) return null;
  const value = line.slice(FOUNDER_BULLET_LABELS.advisor.length).trim();
  if (!value || value === NOT_CAPTURED) return null;
  const email = /<([^>]+)>/.exec(value)?.[1]?.trim() ?? "";
  const name = value.replace(/<[^>]*>/, "").trim();
  return name || email ? { name, email } : null;
}

export function renderFounderRecord(
  block: OnboardingBlock,
  inputs: { subscriptionId?: string; azIdentity?: { name: string; email: string } },
  now: string = new Date().toISOString(),
): { founderMd: string; memoryIndexLine: string } {
  const pick = (...vals: (string | undefined)[]) => vals.map((v) => v?.trim()).find(Boolean) ?? "";
  const founderName = pick(block.founder_name, inputs.azIdentity?.name);
  const founderEmail = pick(block.founder_email, inputs.azIdentity?.email);
  // advisor_name is required by the internal type, so a short-intake block normalizes it to
  // "" — identical to a long-questionnaire block where the founder was asked and left it
  // blank. That ambiguity is harmless here: the bullet below is keyed off the combination of
  // name + email, and the short intake does ask for advisor_email directly, so an empty name
  // never causes a real answer to be hidden — it only ever drops an empty string.
  const advisorName = (block.advisor_name ?? "").trim();
  const advisorEmail = (block.advisor_email ?? "").trim();
  const subscription = (inputs.subscriptionId ?? "").trim();

  const advisorRendered = renderAdvisor(advisorName, advisorEmail, NOT_CAPTURED);

  const request = block.pending_requests?.[0];
  const requestLines = request === undefined ? [] : (() => {
    const modelLabel = request.model.trim() ? `\`${request.model.trim()}\`` : "(model not recorded)";
    const consentLabel = request.consent === "submit" ? "submit the request" : "prepare it only, don't submit";
    const addressLine = request.company_address === null
      ? "not provided — the founder skipped it"
      : [request.company_address.address, request.company_address.city,
         request.company_address.postal_code, request.company_address.country]
          .map((part) => part.trim()).filter(Boolean).join(", ") || "not provided";
    return [
      ``,
      `## Requested at onboarding`,
      ``,
      `_The founder asked for this during setup. Act on it when they bring it up._`,
      ``,
      `- **What:** ${request.type} increase for ${modelLabel}`,
      `- **Region:** ${request.region.trim() || REGION_UNKNOWN_AT_INTAKE}`,
      `- **Consent:** ${consentLabel}`,
      `- **Company address:** ${addressLine}`,
    ];
  })();

  const founderMd = [
    `---`,
    `type: memory`,
    `title: "Founder & install context"`,
    `created: ${now}`,
    `updated: ${now}`,
    `tags: [founder, install-context, onboarding]`,
    `origin: operator`,
    `---`,
    ``,
    `# Founder & install context`,
    ``,
    `_Seeded at install from your Azure identity + what you confirmed. Authoritative — read it; don't rewrite it (origin: operator)._`,
    ``,
    `- **Founder:** ${founderName || NOT_CAPTURED}`,
    `- **Founder email (company_email):** ${founderEmail || NOT_CAPTURED}`,
    `- **Microsoft Startup Advisor (SA):** ${advisorRendered}`,
    `- **Azure subscription:** ${subscription || NOT_CAPTURED}`,
    ...bullet("Team size", block.team_size),
    ...requestLines,
    ``,
  ].join("\n");

  const idxAdvisor = advisorEmail || advisorName || "—";
  const idxSub = subscription ? `${subscription.slice(0, 8)}…` : "—";
  const memoryIndexLine =
    `- \`${FOUNDER_RECORD_PATH}\` — **Founder & install context**: ${founderName || "founder"} · advisor ${idxAdvisor} · sub ${idxSub}. (seeded from onboarding)`;
  return { founderMd, memoryIndexLine };
}

/** Compare two rendered founder records ignoring the volatile created/updated lines. */
export function founderRecordBodyEquals(a: string, b: string): boolean {
  const strip = (s: string) => stripVolatileFrontmatterTimestamps(s).trim();
  return strip(a) === strip(b);
}
